import LZString from 'lz-string';
import { useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

/**
 * Page-specific transform applied to columns resolved from the URL/storage:
 * migrate legacy names and/or filter to the page's valid (non-hidden) set.
 * Returning an empty array signals "nothing valid", so the hook falls back to
 * the supplied default columns. Pages without bespoke rules omit this and get
 * the generic `validColumnNames` filtering instead.
 */
export type ResolvedColumnProcessor = (columns: string[]) => string[];

const isValidColumns = (columns: unknown): columns is string[] => {
  if (!Array.isArray(columns)) {
    return false;
  }

  return columns.every((col) => typeof col === 'string');
};

const getDefaultColumns = (defaultColumns?: string[]): string[] =>
  defaultColumns && defaultColumns.length > 0 ? defaultColumns : [];

const areArraysEqual = (left: string[], right: string[]): boolean => {
  if (left.length !== right.length) {
    return false;
  }

  return left.every((value, index) => value === right[index]);
};

const getUniqueColumns = (columns: string[]): string[] => {
  const seen = new Set<string>();
  return columns.filter((column) => {
    if (seen.has(column)) {
      return false;
    }
    seen.add(column);
    return true;
  });
};

const filterValidColumns = (
  columns: string[],
  validColumnNames?: string[]
): string[] => {
  if (!validColumnNames || validColumnNames.length === 0) {
    return columns;
  }

  const validSet = new Set(validColumnNames);
  return columns.filter((col) => validSet.has(col));
};

const getFallbackColumns = (
  defaultColumns?: string[],
  validColumnNames?: string[]
): string[] => {
  const defaults = getDefaultColumns(defaultColumns);
  const filteredDefaults = filterValidColumns(defaults, validColumnNames);

  if (validColumnNames && validColumnNames.length > 0) {
    return filteredDefaults;
  }

  return defaults;
};

const getColumnsFromCompressed = (
  compressed: string,
  source: 'url' | 'storage'
): string[] | undefined => {
  const decompressed =
    source === 'url'
      ? LZString.decompressFromEncodedURIComponent(compressed)
      : LZString.decompressFromUTF16(compressed);

  if (!decompressed) {
    return undefined;
  }

  try {
    const parsed: unknown = JSON.parse(decompressed);
    if (!isValidColumns(parsed) || parsed.length === 0) {
      return undefined;
    }

    return parsed;
  } catch {
    return undefined;
  }
};

type ResolveInitialColumnsArgs = {
  locationSearch: string;
  storageKey: string;
  defaultColumns?: string[];
  validColumnNames?: string[];
  processResolvedColumns?: ResolvedColumnProcessor;
};

export const resolveInitialTableColumns = ({
  locationSearch,
  storageKey,
  defaultColumns,
  validColumnNames,
  processResolvedColumns,
}: ResolveInitialColumnsArgs): string[] => {
  const params = new URLSearchParams(locationSearch);
  const columnsParam = params.get('columns');
  const fallbackSelection = getFallbackColumns(
    defaultColumns,
    validColumnNames
  );
  const storageColumnsRaw = sessionStorage.getItem(storageKey);
  const fromUrl = columnsParam
    ? getColumnsFromCompressed(columnsParam, 'url')
    : undefined;
  const fromStorage = storageColumnsRaw
    ? getColumnsFromCompressed(storageColumnsRaw, 'storage')
    : undefined;
  const resolvedColumns = fromUrl ?? fromStorage;

  if (!resolvedColumns) {
    return getUniqueColumns(fallbackSelection);
  }

  const uniqueResolvedColumns = getUniqueColumns(resolvedColumns);

  if (processResolvedColumns) {
    const validatedColumns = processResolvedColumns(uniqueResolvedColumns);

    if (validatedColumns.length > 0) {
      return getUniqueColumns(validatedColumns);
    }

    return getUniqueColumns(getDefaultColumns(defaultColumns));
  }

  const filteredColumns = filterValidColumns(
    uniqueResolvedColumns,
    validColumnNames
  );

  if (filteredColumns.length > 0) {
    return getUniqueColumns(filteredColumns);
  }

  if (fallbackSelection.length > 0) {
    return getUniqueColumns(fallbackSelection);
  }

  return [];
};

type UseTableColumnSyncOptions = {
  skipInitialHydration?: boolean;
  processResolvedColumns?: ResolvedColumnProcessor;
};

export const useTableColumnSync = (
  selectedColumns: string[],
  setSelectedColumns: (cols: string[]) => void,
  storageKey: string,
  defaultColumns?: string[],
  validColumnNames?: string[],
  options?: UseTableColumnSyncOptions
) => {
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const { skipInitialHydration = false, processResolvedColumns } =
    options || {};

  // Load from URL on mount
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency list
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const columnsParam = params.get('columns');
    const fromUrl = columnsParam
      ? getColumnsFromCompressed(columnsParam, 'url')
      : undefined;

    const shouldRemoveInvalidUrl = !fromUrl && Boolean(columnsParam);

    if (shouldRemoveInvalidUrl) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('columns');
          return next;
        },
        { replace: true }
      );
    }

    if (skipInitialHydration) {
      return;
    }
    setSelectedColumns(
      resolveInitialTableColumns({
        locationSearch: location.search,
        storageKey,
        defaultColumns,
        validColumnNames,
        processResolvedColumns,
      })
    );
  }, []);

  // Sync to URL when columns change
  useEffect(() => {
    const uniqueSelectedColumns = getUniqueColumns(selectedColumns);
    const compressed =
      uniqueSelectedColumns.length > 0
        ? LZString.compressToEncodedURIComponent(
            JSON.stringify(uniqueSelectedColumns)
          )
        : undefined;

    if (uniqueSelectedColumns.length === 0) {
      sessionStorage.removeItem(storageKey);
    } else {
      sessionStorage.setItem(
        storageKey,
        LZString.compressToUTF16(JSON.stringify(uniqueSelectedColumns))
      );
    }

    const matchesCurrentUrl = (() => {
      const params = new URLSearchParams(location.search);
      if (!compressed) {
        return !params.has('columns');
      }
      return params.get('columns') === compressed;
    })();

    if (matchesCurrentUrl) {
      return;
    }

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (!compressed) {
          next.delete('columns');
        } else {
          next.set('columns', compressed);
        }
        return next;
      },
      { replace: true }
    );
  }, [selectedColumns, location.search, setSearchParams, storageKey]);

  useEffect(() => {
    if (!validColumnNames || validColumnNames.length === 0) {
      return;
    }

    const filtered = filterValidColumns(selectedColumns, validColumnNames);
    const fallbackSelection = getFallbackColumns(
      defaultColumns,
      validColumnNames
    );
    const nextColumns = (() => {
      if (filtered.length > 0) {
        return filtered;
      }

      if (fallbackSelection.length > 0) {
        return fallbackSelection;
      }

      return [];
    })();

    const uniqueSelectedColumns = getUniqueColumns(selectedColumns);
    const uniqueNextColumns = getUniqueColumns(nextColumns);

    if (areArraysEqual(uniqueSelectedColumns, uniqueNextColumns)) {
      return;
    }

    setSelectedColumns(uniqueNextColumns);
  }, [selectedColumns, validColumnNames, defaultColumns, setSelectedColumns]);
};
